import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Slider } from './ui/slider';
import { Input } from './ui/input';
import { publicContentAPI } from '../lib/api';
import { 
  Calculator, 
  GraduationCap, 
  Building2,
  Tag,
  DollarSign,
  TrendingDown,
  Clock,
  Loader2
} from 'lucide-react';

// Default pricing tiers (fallback if CMS has no data)
const DEFAULT_PRICING = {
  highSchool: {
    label: 'High School',
    icon: GraduationCap,
    tiers: [
      { minHours: 1, maxHours: 5, rate: 45, discount: 0 },
      { minHours: 6, maxHours: 9, rate: 40, discount: 11 },
      { minHours: 10, maxHours: Infinity, rate: 35, discount: 22 }
    ]
  },
  university: {
    label: 'University',
    icon: Building2,
    tiers: [
      { minHours: 1, maxHours: 5, rate: 50, discount: 0 },
      { minHours: 6, maxHours: 9, rate: 45, discount: 10 },
      { minHours: 10, maxHours: Infinity, rate: 40, discount: 20 }
    ]
  }
};

// Icon mapping for CMS pricing levels
const LEVEL_ICONS = {
  highSchool: GraduationCap,
  university: Building2
};

// Export for backward compatibility
export const PRICING = DEFAULT_PRICING;

const PricingCalculator = ({ variant = 'full', onPriceChange, initialHours = 5, initialLevel = 'highSchool' }) => {
  const [studentLevel, setStudentLevel] = useState(initialLevel);
  const [hours, setHours] = useState(initialHours);
  const [pricingConfig, setPricingConfig] = useState(DEFAULT_PRICING);
  const [loading, setLoading] = useState(true);

  // Fetch pricing tiers from CMS on mount
  useEffect(() => {
    const loadPricingTiers = async () => {
      try {
        const res = await publicContentAPI.getPricingTiers();
        const data = res.data;
        
        // If CMS has custom config, use it; otherwise use defaults
        if (data.config && Object.keys(data.config).length > 0) {
          // Merge CMS config with icons
          const mergedConfig = {};
          for (const [key, value] of Object.entries(data.config)) {
            mergedConfig[key] = {
              ...value,
              icon: LEVEL_ICONS[key] || GraduationCap,
              tiers: value.tiers.map(t => ({
                ...t,
                maxHours: t.maxHours >= 999 ? Infinity : t.maxHours
              }))
            };
          }
          setPricingConfig(mergedConfig);
        } else if (data.default_config) {
          // Use default config from API
          const mergedConfig = {};
          for (const [key, value] of Object.entries(data.default_config)) {
            mergedConfig[key] = {
              ...value,
              icon: LEVEL_ICONS[key] || GraduationCap,
              tiers: value.tiers.map(t => ({
                ...t,
                maxHours: t.maxHours >= 999 ? Infinity : t.maxHours
              }))
            };
          }
          setPricingConfig(mergedConfig);
        }
      } catch (err) {
        console.error('Failed to load pricing tiers from CMS, using defaults:', err);
        // Keep default pricing on error
      } finally {
        setLoading(false);
      }
    };
    loadPricingTiers();
  }, []);

  // Helper function to calculate pricing - must be defined before useMemo
  const calculatePricing = useCallback((tier, levelData, level, hrs) => {
    if (!tier) {
      return {
        studentLevel: level,
        hours: hrs,
        hourlyRate: 0,
        totalCost: 0,
        discountPercentage: 0,
        savingsAmount: 0,
        packageName: '',
        standardRate: 0
      };
    }
    
    const hourlyRate = tier.rate;
    const totalCost = hourlyRate * hrs;
    const discountPercentage = tier.discount;
    const standardRate = levelData.tiers[0]?.rate || hourlyRate;
    const savingsAmount = (standardRate * hrs) - totalCost;
    
    // Determine package name
    let packageName = 'Standard Rate';
    if (hrs >= 10) packageName = '10+ Hour Package';
    else if (hrs >= 6) packageName = '6-9 Hour Package';

    return {
      studentLevel: level,
      hours: hrs,
      hourlyRate,
      totalCost,
      discountPercentage,
      savingsAmount,
      packageName,
      standardRate
    };
  }, []);

  // Calculate pricing based on level and hours
  const pricing = useMemo(() => {
    const levelData = pricingConfig[studentLevel];
    if (!levelData) {
      return { studentLevel, hours, hourlyRate: 0, totalCost: 0, discountPercentage: 0, savingsAmount: 0, packageName: '', standardRate: 0 };
    }
    
    const tier = levelData.tiers.find(t => hours >= t.minHours && hours <= t.maxHours);
    if (!tier) {
      // Fallback to last tier if hours exceed all tiers
      const lastTier = levelData.tiers[levelData.tiers.length - 1];
      return calculatePricing(lastTier, levelData, studentLevel, hours);
    }
    
    return calculatePricing(tier, levelData, studentLevel, hours);
  }, [studentLevel, hours, pricingConfig, calculatePricing]);

  // Trigger callback when pricing changes
  useEffect(() => {
    if (onPriceChange && !loading) {
      onPriceChange(pricing);
    }
  }, [pricing, onPriceChange, loading]);

  const handleHoursChange = (value) => {
    const numValue = Array.isArray(value) ? value[0] : parseInt(value) || 1;
    setHours(Math.max(1, Math.min(50, numValue)));
  };

  // Compact variant for booking page
  if (variant === 'compact') {
    if (loading) {
      return (
        <Card className="border-stone-100" data-testid="pricing-calculator-compact">
          <CardContent className="py-8 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
          </CardContent>
        </Card>
      );
    }
    
    return (
      <Card className="border-stone-100" data-testid="pricing-calculator-compact">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Calculator className="h-5 w-5 text-indigo-700" />
            Package Pricing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Student Level Selection */}
          <div className="space-y-2">
            <Label className="text-sm">Student Level</Label>
            <RadioGroup
              value={studentLevel}
              onValueChange={setStudentLevel}
              className="grid grid-cols-2 gap-2"
            >
              {Object.entries(pricingConfig).map(([key, data]) => {
                const Icon = data.icon || GraduationCap;
                return (
                  <div key={key}>
                    <RadioGroupItem
                      value={key}
                      id={`compact-${key}`}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={`compact-${key}`}
                      className="flex items-center gap-2 p-3 rounded-lg border border-stone-200 cursor-pointer transition-all
                        peer-data-[state=checked]:border-indigo-700 peer-data-[state=checked]:bg-indigo-50"
                      data-testid={`level-${key}`}
                    >
                      <Icon className="h-4 w-4 text-indigo-700" />
                      <span className="text-sm font-medium">{data.label}</span>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          {/* Hours Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Hours</Label>
              <span className="text-sm font-semibold text-indigo-700">{hours} hours</span>
            </div>
            <Slider
              value={[hours]}
              onValueChange={handleHoursChange}
              min={1}
              max={30}
              step={1}
              className="w-full"
              data-testid="hours-slider"
            />
          </div>

          {/* Price Summary */}
          <div className="pt-3 border-t border-stone-100 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Package</span>
              <span className="font-medium text-slate-900">{pricing.packageName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Rate</span>
              <span className="font-medium text-slate-900">${pricing.hourlyRate}/hr</span>
            </div>
            {pricing.discountPercentage > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-green-600 flex items-center gap-1">
                  <TrendingDown className="h-3 w-3" />
                  Discount
                </span>
                <span className="font-medium text-green-600">~{pricing.discountPercentage}% off</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-stone-100">
              <span className="font-semibold text-slate-900">Total</span>
              <span className="text-2xl font-bold text-slate-900" data-testid="total-price">
                ${pricing.totalCost.toLocaleString()}
              </span>
            </div>
            {pricing.savingsAmount > 0 && (
              <p className="text-xs text-green-600 text-right">
                You save ${pricing.savingsAmount} with this package!
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full variant for pricing page
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        data-testid="pricing-calculator-full"
      >
        <Card className="border-stone-100 shadow-card overflow-hidden">
          <CardContent className="py-12 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </CardContent>
        </Card>
      </motion.div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="pricing-calculator-full"
    >
      <Card className="border-stone-100 shadow-card overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white">
          <CardTitle className="font-heading text-2xl flex items-center gap-3">
            <Calculator className="h-7 w-7" />
            Pricing Calculator
          </CardTitle>
          <p className="text-indigo-200 mt-2">
            Select your student level and hours to see your personalized rate
          </p>
        </CardHeader>
        <CardContent className="p-6 md:p-8 space-y-8">
          {/* Student Level Selection */}
          <div className="space-y-4">
            <Label className="text-lg font-semibold text-slate-900">1. Select Student Level</Label>
            <RadioGroup
              value={studentLevel}
              onValueChange={setStudentLevel}
              className="grid md:grid-cols-2 gap-4"
            >
              {Object.entries(pricingConfig).map(([key, data]) => {
                const Icon = data.icon || GraduationCap;
                const isSelected = studentLevel === key;
                return (
                  <div key={key}>
                    <RadioGroupItem
                      value={key}
                      id={`full-${key}`}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={`full-${key}`}
                      className={`flex items-center gap-4 p-6 rounded-xl border-2 cursor-pointer transition-all
                        ${isSelected 
                          ? 'border-indigo-700 bg-indigo-50 shadow-md' 
                          : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                        }`}
                      data-testid={`level-select-${key}`}
                    >
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors
                        ${isSelected ? 'bg-indigo-700 text-white' : 'bg-stone-100 text-slate-600'}`}>
                        <Icon className="h-7 w-7" />
                      </div>
                      <div>
                        <span className="text-lg font-semibold text-slate-900 block">{data.label}</span>
                        <span className="text-sm text-slate-500">
                          Starting at ${data.tiers[0]?.rate || 45}/hour
                        </span>
                      </div>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          {/* Hours Selection */}
          <div className="space-y-4">
            <Label className="text-lg font-semibold text-slate-900">2. Select Number of Hours</Label>
            <div className="bg-stone-50 rounded-xl p-6 space-y-6">
              <div className="flex items-center gap-4">
                <Clock className="h-5 w-5 text-indigo-700" />
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={hours}
                  onChange={(e) => handleHoursChange(e.target.value)}
                  className="w-24 text-center text-lg font-semibold"
                  data-testid="hours-input"
                />
                <span className="text-slate-600">hours</span>
              </div>
              <Slider
                value={[hours]}
                onValueChange={handleHoursChange}
                min={1}
                max={50}
                step={1}
                className="w-full"
                data-testid="hours-slider-full"
              />
              <div className="flex justify-between text-xs text-slate-500">
                <span>1</span>
                <span>10</span>
                <span>20</span>
                <span>30</span>
                <span>40</span>
                <span>50</span>
              </div>
            </div>
          </div>

          {/* Pricing Tiers Display */}
          <div className="space-y-4">
            <Label className="text-lg font-semibold text-slate-900">Available Packages</Label>
            <div className="grid md:grid-cols-3 gap-4">
              {(pricingConfig[studentLevel]?.tiers || []).map((tier, index) => {
                const isActive = hours >= tier.minHours && (tier.maxHours === Infinity || tier.maxHours >= 999 ? true : hours <= tier.maxHours);
                return (
                  <div
                    key={index}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      isActive 
                        ? 'border-indigo-700 bg-indigo-50 shadow-md' 
                        : 'border-stone-200 bg-white'
                    }`}
                    data-testid={`tier-${index}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">
                        {tier.minHours === 1 ? '1-5' : tier.minHours === 6 ? '6-9' : '10+'} hours
                      </span>
                      {tier.discount > 0 && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          ~{tier.discount}% OFF
                        </span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-slate-900">${tier.rate}</span>
                      <span className="text-slate-500">/hour</span>
                    </div>
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mt-3 flex items-center gap-1 text-indigo-700 text-sm font-medium"
                      >
                        <Tag className="h-4 w-4" />
                        Current selection
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Price Summary */}
          <motion.div
            key={`${studentLevel}-${hours}`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 md:p-8 text-white"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl">Your Price Summary</h3>
              <span className="px-3 py-1 bg-indigo-600 rounded-full text-sm font-medium">
                {pricing.packageName}
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-white/10 rounded-xl">
                <Clock className="h-6 w-6 mx-auto mb-2 text-indigo-300" />
                <p className="text-sm text-slate-300 mb-1">Hours</p>
                <p className="text-2xl font-bold" data-testid="summary-hours">{hours}</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-xl">
                <DollarSign className="h-6 w-6 mx-auto mb-2 text-indigo-300" />
                <p className="text-sm text-slate-300 mb-1">Hourly Rate</p>
                <p className="text-2xl font-bold" data-testid="summary-rate">${pricing.hourlyRate}</p>
              </div>
              <div className="text-center p-4 bg-indigo-600 rounded-xl">
                <Tag className="h-6 w-6 mx-auto mb-2" />
                <p className="text-sm text-indigo-200 mb-1">Total Cost</p>
                <p className="text-3xl font-bold" data-testid="summary-total">${pricing.totalCost.toLocaleString()}</p>
              </div>
            </div>
            {pricing.savingsAmount > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 flex items-center justify-center gap-2 text-green-400"
              >
                <TrendingDown className="h-5 w-5" />
                <span className="font-medium">
                  You save ${pricing.savingsAmount} compared to the standard rate!
                </span>
              </motion.div>
            )}
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export { PricingCalculator };
export default PricingCalculator;
